// src/features/workflow/aiConfig.ts
// Business logic for AI configurability (prompt templates, agent tools).
// Framework-agnostic, zero React imports.

import type { PromptTemplate } from '@core/types/workflow/PromptTemplate';
import type { PromptVariable } from '@core/types/workflow/PromptVariable';
import type { AgentTool } from '@core/types/workflow/AgentTool';
import type { AgentToolKind } from '@core/types/workflow/AgentToolKind';

// --- Agent tool kinds ---

export const agentToolKindMap: Record<string, { labelKey: string; variant: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'default' }> = {
  AGENT_TOOL_KIND_HTTP:         { labelKey: 'workflow.toolKind.http',      variant: 'primary' },
  AGENT_TOOL_KIND_PLATFORM_RPC: { labelKey: 'workflow.toolKind.rpc',       variant: 'success' },
  AGENT_TOOL_KIND_KNOWLEDGE:    { labelKey: 'workflow.toolKind.knowledge', variant: 'warning' },
};

export const agentToolKindOptions: { value: AgentToolKind; labelKey: string }[] = [
  { value: 'AGENT_TOOL_KIND_HTTP',         labelKey: 'workflow.toolKind.http' },
  { value: 'AGENT_TOOL_KIND_PLATFORM_RPC', labelKey: 'workflow.toolKind.rpc' },
  { value: 'AGENT_TOOL_KIND_KNOWLEDGE',    labelKey: 'workflow.toolKind.knowledge' },
];

export const httpMethodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

// --- Prompt variable types ---

export const promptVariableTypeOptions: { value: string; labelKey: string }[] = [
  { value: 'string',  labelKey: 'workflow.varType.string' },
  { value: 'number',  labelKey: 'workflow.varType.number' },
  { value: 'boolean', labelKey: 'workflow.varType.boolean' },
  { value: 'json',    labelKey: 'workflow.varType.json' },
];

export function emptyPromptVariable(): PromptVariable {
  return { name: '', label: '', description: '', type: 'string', required: false, default_value: '' };
}

// --- Slug ---

const SLUG_RE = /^[a-z0-9][a-z0-9._-]*$/;

export function validateSlug(slug: string): string | undefined {
  if (!slug.trim()) return 'validation.required';
  if (!SLUG_RE.test(slug.trim())) return 'workflow.validation.slugFormat';
  return undefined;
}

// --- JSON validation ---

export function validateJsonText(text: string): string | undefined {
  if (!text.trim()) return undefined; // optional
  try {
    JSON.parse(text);
    return undefined;
  } catch {
    return 'workflow.validation.invalidJson';
  }
}

// --- Number parsing (plain clearable text inputs, no steppers) ---

export function parseOptionalNumber(text: string): number | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

export function validateOptionalNumber(text: string): string | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  return Number.isFinite(Number(trimmed)) ? undefined : 'workflow.validation.invalidNumber';
}

// --- Prompt template form ---

export interface PromptTemplateFormData {
  slug: string;
  name: string;
  description: string;
  is_active: boolean;
  system_prompt: string;
  user_prompt: string;
  variables: PromptVariable[];
  model: string;
  temperature: string;
  max_tokens: string;
  response_schema: string;
}

export function emptyPromptTemplateForm(): PromptTemplateFormData {
  return {
    slug: '',
    name: '',
    description: '',
    is_active: true,
    system_prompt: '',
    user_prompt: '',
    variables: [],
    model: '',
    temperature: '',
    max_tokens: '',
    response_schema: '',
  };
}

export function promptTemplateToForm(tpl: PromptTemplate): PromptTemplateFormData {
  return {
    slug: tpl.slug || '',
    name: tpl.name || '',
    description: tpl.description || '',
    is_active: tpl.is_active !== false,
    system_prompt: tpl.system_prompt || '',
    user_prompt: tpl.user_prompt || '',
    variables: (tpl.variables || []).map((v) => ({ ...emptyPromptVariable(), ...v })),
    model: tpl.model || '',
    temperature: tpl.temperature !== undefined && tpl.temperature !== null ? String(tpl.temperature) : '',
    max_tokens: tpl.max_tokens !== undefined && tpl.max_tokens !== null ? String(tpl.max_tokens) : '',
    response_schema: tpl.response_schema || '',
  };
}

export function validatePromptTemplateForm(data: PromptTemplateFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) errors.name = 'validation.required';
  const slugError = validateSlug(data.slug);
  if (slugError) errors.slug = slugError;
  if (!data.model.trim()) errors.model = 'validation.required';
  const tempError = validateOptionalNumber(data.temperature);
  if (tempError) errors.temperature = tempError;
  const tokensError = validateOptionalNumber(data.max_tokens);
  if (tokensError) errors.max_tokens = tokensError;
  const schemaError = validateJsonText(data.response_schema);
  if (schemaError) errors.response_schema = schemaError;
  data.variables.forEach((v, i) => {
    if (!v.name.trim()) errors[`variable_${i}`] = 'validation.required';
  });
  return errors;
}

export function buildPromptTemplatePayload(data: PromptTemplateFormData): PromptTemplate {
  return {
    slug: data.slug.trim(),
    name: data.name.trim(),
    description: data.description.trim(),
    is_active: data.is_active,
    is_builtin: false,
    system_prompt: data.system_prompt,
    user_prompt: data.user_prompt,
    variables: data.variables.map((v) => ({
      name: v.name.trim(),
      label: v.label.trim(),
      description: v.description.trim(),
      type: v.type || 'string',
      required: !!v.required,
      default_value: v.default_value,
    })),
    model: data.model.trim(),
    temperature: parseOptionalNumber(data.temperature),
    max_tokens: parseOptionalNumber(data.max_tokens),
    response_schema: data.response_schema.trim(),
  };
}

// --- Agent tool form ---

export interface HeaderRow {
  key: string;
  value: string;
}

export interface AgentToolFormData {
  slug: string;
  name: string;
  description: string;
  kind: AgentToolKind;
  is_active: boolean;
  url: string;
  http_method: string;
  headers: HeaderRow[];
  parameters_schema: string;
  credential_id: string;
  rpc_method: string;
  knowledge_base_id: string;
}

export function emptyAgentToolForm(): AgentToolFormData {
  return {
    slug: '',
    name: '',
    description: '',
    kind: 'AGENT_TOOL_KIND_HTTP',
    is_active: true,
    url: '',
    http_method: 'POST',
    headers: [],
    parameters_schema: '',
    credential_id: '',
    rpc_method: '',
    knowledge_base_id: '',
  };
}

export function agentToolToForm(tool: AgentTool): AgentToolFormData {
  return {
    slug: tool.slug || '',
    name: tool.name || '',
    description: tool.description || '',
    kind: tool.kind && tool.kind !== 'AGENT_TOOL_KIND_UNSPECIFIED' ? tool.kind : 'AGENT_TOOL_KIND_HTTP',
    is_active: tool.is_active !== false,
    url: tool.url || '',
    http_method: tool.http_method || 'POST',
    headers: Object.entries(tool.headers || {}).map(([key, value]) => ({ key, value })),
    parameters_schema: tool.parameters_schema || '',
    credential_id: tool.credential?.id || '',
    rpc_method: tool.rpc_method || '',
    knowledge_base_id: tool.knowledge_base?.id || '',
  };
}

export function validateAgentToolForm(data: AgentToolFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) errors.name = 'validation.required';
  const slugError = validateSlug(data.slug);
  if (slugError) errors.slug = slugError;
  if (!data.description.trim()) errors.description = 'validation.required';
  if (data.kind === 'AGENT_TOOL_KIND_HTTP') {
    if (!data.url.trim()) errors.url = 'validation.required';
    const schemaError = validateJsonText(data.parameters_schema);
    if (schemaError) errors.parameters_schema = schemaError;
  }
  if (data.kind === 'AGENT_TOOL_KIND_PLATFORM_RPC' && !data.rpc_method.trim()) {
    errors.rpc_method = 'validation.required';
  }
  return errors;
}

export interface EntityScope {
  platformId: string;
  tenantId: string;
  productId: string;
}

function refEntity(id: string, typeName: string, scope: EntityScope) {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

export function buildAgentToolPayload(data: AgentToolFormData, scope: EntityScope): AgentTool {
  const isHttp = data.kind === 'AGENT_TOOL_KIND_HTTP';
  const headers: Record<string, string> = {};
  for (const row of data.headers) {
    if (row.key.trim()) headers[row.key.trim()] = row.value;
  }
  return {
    slug: data.slug.trim(),
    name: data.name.trim(),
    description: data.description.trim(),
    kind: data.kind,
    is_active: data.is_active,
    is_builtin: false,
    url: isHttp ? data.url.trim() : '',
    http_method: isHttp ? data.http_method : '',
    headers: isHttp ? headers : {},
    parameters_schema: isHttp ? data.parameters_schema.trim() : '',
    credential: isHttp && data.credential_id ? refEntity(data.credential_id, 'credential', scope) : undefined,
    rpc_method: data.kind === 'AGENT_TOOL_KIND_PLATFORM_RPC' ? data.rpc_method.trim() : '',
    knowledge_base:
      data.kind === 'AGENT_TOOL_KIND_KNOWLEDGE' && data.knowledge_base_id.trim()
        ? refEntity(data.knowledge_base_id.trim(), 'knowledge_base', scope)
        : undefined,
  };
}

// --- Test panel helpers ---

// Split rendered prompt text into plain segments and unresolved placeholder
// segments so the UI can highlight what did not resolve.
export interface RenderedSegment {
  text: string;
  unresolved: boolean;
}

export function segmentRenderedPrompt(text: string, unresolved: string[]): RenderedSegment[] {
  if (!text) return [];
  if (!unresolved || unresolved.length === 0) return [{ text, unresolved: false }];
  const names = unresolved.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const splitter = new RegExp(`(\\{\\{\\s*(?:${names.join('|')})\\s*\\}\\})`, 'g');
  const matcher = new RegExp(`^\\{\\{\\s*(?:${names.join('|')})\\s*\\}\\}$`);
  return text
    .split(splitter)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, unresolved: matcher.test(part) }));
}

// Initial variable values for the test panel, seeded from declared defaults.
export function initialTestValues(variables: PromptVariable[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const v of variables) {
    if (v.name) values[v.name] = v.default_value || '';
  }
  return values;
}
